import React, { ReactNode, useState } from "react";
import { router } from "@inertiajs/react";
import { longDate, money, time } from "@/helpers/landingFormat";
import { relativeRoute } from "@/helpers/relativeRoute";
import { LandingProps } from "@/types/Landing";

/**
 * Logic shared by the landing pages of the domains (Pages/Landing/ObituaryLanding): the checked papers, starting the
 * order flow with them and the content that depends on how the page was entered (by paper or by place).
 * T is what the order is started with, to tell which button is loading.
 */
export default <T extends string | number>(
  {paper, place, priceAverage, nextEdition, otherPapers}: Pick<LandingProps, 'paper' | 'place' | 'priceAverage' | 'nextEdition' | 'otherPapers'>,
  extraHighlights: ReactNode[] = [],
) => {
  const [starting, setStarting] = useState<T | null>(null);

  // Papers checked in the request form. Entered by paper: that paper is checked by default.
  // Entered by place: nothing is checked, the papers in the place are chosen by the customer.
  const defaultPapers = place ? [] : [paper.id];
  const [selectedPapers, setSelectedPapers] = useState<number[]>(defaultPapers);
  const togglePaper = (paperId: number) => setSelectedPapers((selected) => selected.includes(paperId)
    ? selected.filter((id) => id !== paperId)
    : [...selected, paperId]);

  // Skip the product select step. Entered by paper: start the order with the checked papers already selected.
  // Entered by place: always start without papers, they are chosen in the order flow.
  const start = (key: T, data: Record<string, string | number>) => {
    const [startPaper, ...extraPapers] = place ? [] : (selectedPapers.length > 0 ? selectedPapers : defaultPapers);
    setStarting(key);
    router.post(relativeRoute('order.quick-start', startPaper ? {paper: startPaper} : {}), {
      ...data,
      papers: extraPapers,
    }, {
      onFinish: () => setStarting(null),
    });
  };

  // Above the fold. Entered by paper: its price and deadline.
  // Entered by place: nothing about one paper, the papers in the place can be chosen.
  const highlights: ReactNode[] = place
    ? [
      <>Kies zelf uit de kranten die in <strong>{place.title}</strong> verschijnen</>,
      <>Plaats je rouwadvertentie in één of meerdere kranten tegelijk</>,
      <>Vraag vrijblijvend een prijs en ontwerp aan</>,
      ...extraHighlights,
    ]
    : [
      ...(priceAverage ? [<>Prijs gemiddeld <strong>€ {money(priceAverage)}</strong> op basis van meest gekozen formaat</>] : []),
      <>
        Aanleveren vóór <strong>{longDate(nextEdition.deadline)} {time(nextEdition.deadline)} uur</strong> voor
        plaatsing op <strong>{longDate(nextEdition.date)}</strong>
      </>,
      ...(otherPapers.length > 0 ? [<>Kies eventueel ook andere kranten</>] : []),
      ...extraHighlights,
    ];

  return {
    selectedPapers,
    togglePaper,
    starting,
    start,
    highlights,
    // what the page is about: the place it was entered by, otherwise the paper
    subject: place ? place.title : paper.title,
    // data-sa-location of the analytics events
    location: place ? 'seo_plaats' : 'seo_krant',
  };
}
